import { memo, useCallback, useEffect, useRef, useState } from 'react'
import { without } from 'lodash-es'
import { Table, Checkbox, LoadingOverlay } from '@mantine/core'
import { showNotification } from '@mantine/notifications'
import { Pagination, Button } from '@ow/comps'
import { useAppEx } from '@vt/hooks'
import { dayjsInit } from '@ow/utils'
import {
  fetchDownloadList,
  destoryDownloadTask,
} from '@vt/apis/lib-ex/v2/downloadCenter'
import styled from '@emotion/styled'
import Filter from './filter'
import { getDslType, useLazyLoading } from './utils'
import { PAGE_SIZE_OPTIONS, DEFAULT_PAGE_SIZE, STATUS_ENUM } from './config'
import Operator from './operator'
import SideBar from './sidebar'

const StyledStatus = styled.div`
  display: flex;
  align-items: center;
  color: var(--ow-color-gray-100);
  i {
    display: inline-block;
    width: 8px;
    height: 8px;
    border-radius: 4px;
    background: var(--ow-color-neutral-600);
    margin-right: 22px;
  }
`

const List = () => {
  const { appId } = useAppEx()
  const [pageInfo, setPageInfo] = useState({
    page: 1,
    total: 0,
    size: DEFAULT_PAGE_SIZE,
  })
  const [checkList, setCheckList] = useState([])
  const [dataList, setDataList] = useState([])
  const [sideBarVal, setSideBarVal] = useState('')
  const [loading, setLoading] = useLazyLoading()
  const filterRef = useRef({})

  const onCheck = useCallback((e) => {
    const { checked, name } = e.target
    if (checked) {
      setCheckList((pre) => [...pre, name])
    } else {
      setCheckList((pre) => without(pre, name))
    }
  }, [])

  const onCheckAll = useCallback(
    (e) => {
      const { checked } = e.target
      if (checked) {
        setCheckList(dataList.map((item) => item.id.toString()))
      } else {
        setCheckList([])
      }
    },
    [dataList]
  )

  const fetchData = useCallback(async (page, size) => {
    setLoading(true)
    setCheckList([])
    const res = await fetchDownloadList({
      appId,
      page: page - 1,
      per: size,
      ...filterRef.current,
      sidebar: sideBarVal,
    })
    const { code, message, data, pagination } = res || {}
    setLoading(false)
    if (code === 0) {
      setDataList(data)
      setPageInfo({ page, size, total: pagination.total })
    } else {
      showNotification({
        title: message || '加载失败',
        color: 'red',
      })
    }
  }, [])

  const refresh = useCallback(() => {
    fetchData(pageInfo.page, pageInfo.size)
  }, [pageInfo])

  const onFilter = useCallback(
    (filter) => {
      filterRef.current = filter
      fetchData(1, pageInfo.size)
    },
    [pageInfo.size]
  )

  const changePage = useCallback(
    (page) => {
      fetchData(page, pageInfo.size)
    },
    [pageInfo.size]
  )

  const changeSize = useCallback((size) => {
    fetchData(1, size)
  }, [])

  const deleteTask = async () => {
    if (!checkList.length) return
    const res = await destoryDownloadTask(checkList)
    showNotification({
      title: res.message,
    })
    fetchData(1, pageInfo.size)
  }

  const onSideBarChange = (val) => {
    setSideBarVal(val)
    // TODO: 暂时不走接口
    // fetchData(1, pageInfo.size)
  }

  useEffect(() => {
    fetchData(1, DEFAULT_PAGE_SIZE)
  }, [])

  return (
    <div className="flex flex-col flex-1 p-4 gap-4 overflow-hidden">
      <div className="bg-white p-4">
        <Filter onFilter={onFilter} />
      </div>
      <div className="flex flex-1 gap-4 overflow-hidden">
        <SideBar onChange={onSideBarChange} />
        <div className="h-full flex flex-1 flex-col gap-3 p-4 overflow-hidden bg-white ">
          <LoadingOverlay visible={loading} />
          <div className="flex-1 overflow-auto">
            <Table>
              <thead className="sticky top-0 z-10">
                <tr className="bg-primary-50">
                  <th>
                    <Checkbox
                      checked={
                        checkList.length === dataList.length &&
                        dataList.length > 0
                      }
                      indeterminate={
                        checkList.length < dataList.length &&
                        checkList.length > 0
                      }
                      onChange={onCheckAll}
                    />
                  </th>
                  <th>编号</th>
                  <th>导出内容</th>
                  <th>下载方式</th>
                  <th>创建时间</th>
                  <th>数据状态</th>
                  <th style={{ width: 150 }}>操作方式</th>
                </tr>
              </thead>
              <tbody>
                {dataList.map((item) => {
                  const statusConfig = STATUS_ENUM[item.state || 'READY'] || {}
                  return (
                    <tr key={item.id}>
                      <td>
                        <Checkbox
                          checked={checkList.includes(item.id.toString())}
                          name={item.id}
                          onChange={onCheck}
                        />
                      </td>
                      <td>{item.id}</td>
                      <td>{getDslType(item.dsl)}</td>
                      <td>{item.taskType}</td>
                      <td>
                        {dayjsInit(item.createdAt).format(
                          'YYYY/MM/DD HH:mm:ss'
                        )}
                      </td>
                      <td>
                        <StyledStatus>
                          <i
                            style={{
                              backgroundColor: `${statusConfig.bgcolor}`,
                            }}
                          />
                          {statusConfig.label}
                        </StyledStatus>
                      </td>
                      <td>
                        <Operator task={item} refresh={refresh} />
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </Table>
          </div>
          <div className="flex justify-between items-center">
            <div className="flex gap-4 items-center">
              <span className="text-sm">已选择 {checkList.length} 条数据</span>
              <Button onClick={deleteTask} stroke>
                批量删除
              </Button>
            </div>
            <Pagination
              defaultPage={pageInfo.page}
              pageSize={pageInfo.size}
              total={pageInfo.total}
              pageSizeOptions={PAGE_SIZE_OPTIONS}
              showPageSize
              showJumpPage={false}
              onPageNumChange={changePage}
              onPageSizeChange={changeSize}
            />
          </div>
        </div>
      </div>
    </div>
  )
}

export default memo(List)
